import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { CalculateInputTrigger } from "components/ui/CalculateInputTrigger/CalculateInputTrigger";
import { Checkbox } from "components/ui/Checkbox";
import { Select } from "components/ui/Select/Select";
import { getCdmField, isCdmFieldMandatory } from "features/cdm/utils/cdmMatrix";
import {
  isLabValueOutOfRange,
  withLabRange,
} from "features/cdm/utils/withLabRange";
import { CreatinineClearanceCalculatorModal } from "features/creatinineClearance/CreatinineClearanceCalculatorModal/CreatinineClearanceCalculatorModal";
import { onInvalidSubmit } from "utils/reportFormErrors";

import { ItemFormWrapper } from "../../../../../components/ItemFormWrapper/ItemFormWrapper";
import {
  COUNT_OPTIONS,
  CREATININE_CLEARANCE_HINT,
  ECG_RESULT_OPTIONS,
  ECHO_RESULT_OPTIONS,
  EDUCATION_PROVIDED_OPTIONS,
  FOOT_AND_LIMB_REVIEW_PAIRS,
  HBA1C_OUT_OF_RANGE_WARNING,
  REFERRAL_DETAILS_OPTIONS,
  REFERRED_TO_DIABETES_PREVENTION_OPTIONS,
  REFERRED_TO_EDUCATION_OPTIONS,
  SPIROMETRY_RESULT_OPTIONS,
  YES_NO_OPTIONS,
  YES_NO_PATIENT_DECLINED_OPTIONS,
} from "../../../../../constants/formOptions";
import style from "./TestAndMedicationForm.module.css";

// HSE CDM Message Spec (19-03-2024) — alert must display on every review.
// Reconciled to the Socrates wording (KIN-1354).
const NT_PROBNP_ALERT =
  "Indications for NTproBNP testing: In those with new symptoms of possible heart failure it can aid in diagnosis. In established heart failure to help determine if deterioration of symptoms is due to heart failure. In asymptomatic patients at risk for heart failure to identify those most at risk. Repeat testing is not required unless new symptoms.";

// Ref 55b: Non-HDL Cholesterol is auto-calculated as Total Cholesterol − HDL.
// Sentinel strings (Not Available / Not clinically necessary) parse to NaN, so
// the field clears automatically when either input is non-numeric.
const computeNonHdl = (total, hdl) => {
  const totalValue = parseFloat(total);
  const hdlValue = parseFloat(hdl);

  if (isNaN(totalValue) || isNaN(hdlValue)) return "";

  const nonHdl = totalValue - hdlValue;

  // Non-HDL is clinically impossible below zero (Total < HDL). Blank the field
  // rather than show a negative value; the Total < HDL inconsistency is flagged
  // as a validation error on the HDL field (see superRefine).
  return nonHdl < 0 ? "" : nonHdl.toFixed(1);
};

export const TestAndMedicationForm = ({
  onBack,
  onContinue,
  data,
  isReview = false,
  type,
  registrationReasons,
}) => {
  const reasons = registrationReasons ?? [];
  const hasDiabetes = reasons.includes("DIABETES_TYPE_2");
  const hasAsthmaOrCopd =
    reasons.includes("ASTHMA") || reasons.includes("COPD");
  const [isCrclModalOpen, setIsCrclModalOpen] = useState(false);

  const shouldShowHemoglobin = getCdmField(
    registrationReasons,
    "haemoglobin",
    type
  );
  const shouldShowTotalCholesterol = getCdmField(
    registrationReasons,
    "totalCholesterol",
    type
  );
  const shouldShowHdlCholesterol = getCdmField(
    registrationReasons,
    "hdlCholesterol",
    type
  );
  const shouldShowNonHdlCholesterol = getCdmField(
    registrationReasons,
    "nonHdlCholesterol",
    type
  );
  const shouldShowLdlCholesterol = getCdmField(
    registrationReasons,
    "ldlCholesterol",
    type
  );
  const shouldShowTriglycerides = getCdmField(
    registrationReasons,
    "triglycerides",
    type
  );
  const shouldShowHba1c = getCdmField(registrationReasons, "hba1c", type);
  const shouldShowFastingGlucose = getCdmField(
    registrationReasons,
    "fastingGlucose",
    type
  );
  const shouldShowSerumCreatinine = getCdmField(
    registrationReasons,
    "serumCreatinine",
    type
  );
  const shouldShowEgfr = getCdmField(registrationReasons, "egfr", type);
  const shouldShowTftConducted = getCdmField(
    registrationReasons,
    "tftConducted",
    type
  );
  const shouldShowLftConducted = getCdmField(
    registrationReasons,
    "lftConducted",
    type
  );
  const shouldShowCreatinineClearance = getCdmField(
    registrationReasons,
    "creatinineClearance",
    type
  );
  const shouldShowNtProBNP = getCdmField(registrationReasons, "ntProBNP", type);
  const shouldShowAcr = getCdmField(registrationReasons, "acr", type);
  // When ACR is mandatory (matrix "Yes"), a value is required — no "Not
  // available" escape. Only the optional ("Yes*") cells offer that checkbox.
  const isAcrMandatory = isCdmFieldMandatory(registrationReasons, "acr", type);
  // Numeric lab fields that expose a "Not available" escape only on their
  // optional ("Yes*") cells — the checkbox is hidden when the field is mandatory.
  const isHaemoglobinMandatory = isCdmFieldMandatory(
    registrationReasons,
    "haemoglobin",
    type
  );
  const isLdlMandatory = isCdmFieldMandatory(
    registrationReasons,
    "ldlCholesterol",
    type
  );
  const isEgfrMandatory = isCdmFieldMandatory(registrationReasons, "egfr", type);
  const isFastingGlucoseMandatory = isCdmFieldMandatory(
    registrationReasons,
    "fastingGlucose",
    type
  );
  const isNtProBNPMandatory = isCdmFieldMandatory(
    registrationReasons,
    "ntProBNP",
    type
  );
  const shouldShowEcgSinceLastReview = getCdmField(
    registrationReasons,
    "ecg",
    type
  );
  const shouldShowEcgResult = getCdmField(registrationReasons, "ecg", type);
  const shouldShowEchoSinceLastReview = getCdmField(
    registrationReasons,
    "echocardiography",
    type
  );
  const shouldShowEchoResult = getCdmField(
    registrationReasons,
    "echocardiography",
    type
  );
  const shouldShowSpirometrySinceLastReview = getCdmField(
    registrationReasons,
    "spirometry",
    type
  );
  const shouldShowSpirometryResult = getCdmField(
    registrationReasons,
    "spirometry",
    type
  );
  const shouldShowDiabetesRelatedAmputation = getCdmField(
    registrationReasons,
    "amputation",
    type
  );
  const shouldShowAmputationYear = getCdmField(
    registrationReasons,
    "amputation",
    type
  );
  const shouldShowFootAndLimbReviewFindings = getCdmField(
    registrationReasons,
    "footAndLimbReview",
    type
  );
  const shouldShowReferredForRetinaScreening = getCdmField(
    registrationReasons,
    "retinalScreen",
    type
  );
  const shouldShowRetinaScreeningUpToDate = getCdmField(
    registrationReasons,
    "retinalScreen",
    type
  );

  const shouldShowDiagnosticinvestigation =
    shouldShowEcgResult || shouldShowEchoResult || shouldShowSpirometryResult;

  const shouldShowInvestigation =
    shouldShowHemoglobin ||
    shouldShowTotalCholesterol ||
    shouldShowHdlCholesterol ||
    shouldShowLdlCholesterol ||
    shouldShowTriglycerides ||
    shouldShowHba1c ||
    shouldShowSerumCreatinine ||
    shouldShowEgfr ||
    shouldShowTftConducted ||
    shouldShowTftConducted ||
    shouldShowLftConducted ||
    shouldShowFastingGlucose ||
    shouldShowCreatinineClearance ||
    shouldShowNtProBNP ||
    shouldShowAcr;

  // Both mandatory ("Yes") and clinically-indicated ("Yes*") numeric lab cells
  // are required: the clinician must enter a value OR tick "Not available"
  // (which sets a non-empty sentinel that satisfies the check). The only
  // difference between M and O is the "Not available" checkbox — shown for O,
  // hidden for M (see the per-field `is…Mandatory` gates in the render).
  const labSchema = (label, name) =>
    withLabRange(z.string().min(1, `${label} is required`), name, label);

  const formSchema = z
    .object({
      // Haemoglobin (FBC) is the one numeric lab that does NOT accept the "Not
      // Available" sentinel per the HL7 spec (allowed only for LDL, Fasting
      // Glucose, eGFR, ACR, NT-proBNP). Its sole optional cell (asthma reg) has
      // no valid NA escape, so it stays required-when-mandatory / optional-when-
      // clinically-indicated rather than required-or-NA.
      // Haemoglobin is the one numeric lab with no "Not available" option in the
      // HL7 spec, so it can't be "required value-or-NA" like the other Yes* labs.
      // When mandatory ("Yes") it's required; on its only optional ("Yes*") cell
      // (asthma registration) it stays optional — there is no NA escape, so we
      // don't force a value the GP may genuinely not have.
      ...(shouldShowHemoglobin
        ? {
            haemoglobin: withLabRange(
              isHaemoglobinMandatory
                ? z.string().min(1, "Haemoglobin is required")
                : z.string().optional(),
              "haemoglobin",
              "Haemoglobin"
            ),
          }
        : {}),
      ...(shouldShowTotalCholesterol
        ? {
            totalCholesterol: labSchema(
              "Total cholesterol",
              "totalCholesterol"
            ),
          }
        : {}),
      ...(shouldShowHdlCholesterol
        ? { hdlCholesterol: labSchema("HDL cholesterol", "hdlCholesterol") }
        : {}),
      // Non-HDL Cholesterol is auto-calculated (disabled) — derived, so it is
      // never user-required and clears when its inputs are non-numeric.
      ...(shouldShowNonHdlCholesterol
        ? { nonHdlCholesterol: z.string().optional() }
        : {}),
      ...(shouldShowLdlCholesterol
        ? { ldlCholesterol: labSchema("LDL cholesterol", "ldlCholesterol") }
        : {}),
      ...(shouldShowTriglycerides
        ? { triglycerides: labSchema("Triglycerides", "triglycerides") }
        : {}),

      // HbA1c is NOT range-blocked: the HL7 spec records an out-of-range value
      // (outside 28–150 mmol/mol) as 0 rather than rejecting it. The raw value
      // is still submitted; an inline warning is shown below the field instead.
      ...(shouldShowHba1c
        ? { hba1c: z.string().min(1, "HbA1c is required") }
        : {}),
      ...(shouldShowSerumCreatinine
        ? {
            serumCreatinine: withLabRange(
              z.string().min(1, "Serum creatinine is required"),
              "serumCreatinine",
              "Serum creatinine"
            ),
          }
        : {}),
      // Required per the Investigations Matrix, by condition × review type
      // (isCdmFieldMandatory): Diabetes & AFib = all 3; COPD = reg only; HF/IHD/
      // Stroke/TIA = reg + annual; Asthma = none. "Not available" satisfies it.
      ...(shouldShowEgfr ? { egfr: labSchema("eGFR", "egfr") } : {}),
      ...(shouldShowTftConducted
        ? { tftConducted: z.string().min(1, "Please select Yes or No for TFT") }
        : {}),
      ...(shouldShowLftConducted
        ? { lftConducted: z.string().min(1, "Please select Yes or No for LFT") }
        : {}),
      // Optional ("Yes*") on Diabetes registration — required value-or-NA.
      ...(shouldShowFastingGlucose
        ? { fastingGlucose: labSchema("Fasting glucose", "fastingGlucose") }
        : {}),
      ...(shouldShowCreatinineClearance
        ? {
            creatinineClearance: z
              .string()
              .min(1, "Creatinine clearance is required"),
          }
        : {}),
      ...(shouldShowNtProBNP
        ? { ntProBNP: labSchema("NT-ProBNP", "ntProBNP") }
        : {}),
      // Required per the Investigations Matrix by condition × review type
      // (isCdmFieldMandatory) — e.g. mandatory on Diabetes registration. The
      // "Not Available" checkbox sets a non-empty value, which satisfies it.
      ...(shouldShowAcr ? { acr: labSchema("ACR", "acr") } : {}),
      ...(shouldShowEcgSinceLastReview
        ? {
            ecgSinceLastReview: z
              .string()
              .min(1, "ECG since last review is required"),
            ecgResult: z.string().optional(),
          }
        : {}),
      ...(shouldShowEchoSinceLastReview
        ? {
            echoSinceLastReview: z
              .string()
              .min(1, "Echocardiography since last review is required"),
            echoResult: z.string().optional(),
          }
        : {}),
      educationProvided: z.string().min(1, "Education provided is required"),
      referredToEducation: z
        .string()
        .min(1, "Referred to education is required"),
      ...(hasDiabetes
        ? {
            referredToDiabetesPrevention: z
              .string()
              .min(1, "Referred to diabetes prevention is required"),
          }
        : {}),
      referralDetails: z.string().optional(),
      ...(shouldShowSpirometrySinceLastReview
        ? {
            spirometrySinceLastReview: z
              .string()
              .min(1, "Spirometry since last review is required"),
            spirometryResult: z.string().optional(),
          }
        : {}),

      ...(shouldShowDiabetesRelatedAmputation
        ? {
            diabetesRelatedAmputation: z
              .string()
              .min(1, "Diabetes related amputation is required"),
          }
        : {}),
      // Ref 43: amputation year is required only when an amputation occurred
      // (enforced in superRefine below).
      ...(shouldShowAmputationYear ? { amputationYear: z.string().optional() } : {}),
      ...(shouldShowFootAndLimbReviewFindings
        ? {
            footAndLimbReviewFindings: z
              .array(z.string().min(1))
              .min(6, "All foot and limb review fields are required"),
            // Ref 44: free-text comment box — optional.
            footAndLimbReviewComment: z.string().optional(),
          }
        : {}),
      ...(shouldShowRetinaScreeningUpToDate
        ? {
            retinaScreeningUpToDate: z
              .string()
              .min(1, "Retina screening up to date is required"),
          }
        : {}),
      // Ref 46: only required when retina screening is NOT up to date (Ref 45 = No).
      ...(shouldShowReferredForRetinaScreening
        ? { referredForRetinaScreening: z.string().optional() }
        : {}),

      ...(hasAsthmaOrCopd
        ? {
            exacerbationsLast6Months: z
              .string()
              .min(1, "Exacerbations last 6 months is required"),
          }
        : {}),
    })
    .superRefine((values, ctx) => {
      // Non-HDL Cholesterol (Total − HDL) cannot be negative: Total Cholesterol
      // must be at least HDL Cholesterol. Sentinels ("NOT_CLINICALLY_NECESSARY")
      // parse to NaN and are skipped.
      if (shouldShowTotalCholesterol && shouldShowHdlCholesterol) {
        const totalValue = parseFloat(values.totalCholesterol);
        const hdlValue = parseFloat(values.hdlCholesterol);

        if (!isNaN(totalValue) && !isNaN(hdlValue) && totalValue < hdlValue) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Total Cholesterol cannot be less than HDL Cholesterol",
            path: ["hdlCholesterol"],
          });
        }
      }

      if (
        shouldShowEcgSinceLastReview &&
        values.ecgSinceLastReview === "YES" &&
        !values.ecgResult
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ECG result is required when ECG was performed",
          path: ["ecgResult"],
        });
      }
      if (
        shouldShowEchoSinceLastReview &&
        values.echoSinceLastReview === "YES" &&
        !values.echoResult
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Echocardiography result is required when echo was performed",
          path: ["echoResult"],
        });
      }
      if (
        shouldShowSpirometrySinceLastReview &&
        values.spirometrySinceLastReview === "YES" &&
        !values.spirometryResult
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Spirometry result is required when spirometry was performed",
          path: ["spirometryResult"],
        });
      }

      if (
        shouldShowAmputationYear &&
        values.diabetesRelatedAmputation === "YES" &&
        !values.amputationYear
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Amputation year is required when an amputation occurred",
          path: ["amputationYear"],
        });
      }

      if (
        shouldShowReferredForRetinaScreening &&
        values.retinaScreeningUpToDate === "NO" &&
        !values.referredForRetinaScreening
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Referred for retina screening is required when retina screening is not up to date",
          path: ["referredForRetinaScreening"],
        });
      }

      const isReferralSelected =
        values.referredToEducation === "YES" ||
        (hasDiabetes && values.referredToDiabetesPrevention === "YES");

      if (isReferralSelected && !values.referralDetails) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Referral details is required when a referral was made",
          path: ["referralDetails"],
        });
      }
    });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      haemoglobin: shouldShowHemoglobin ? data?.haemoglobin || "" : "",
      totalCholesterol: shouldShowTotalCholesterol
        ? data?.totalCholesterol || ""
        : "",
      hdlCholesterol: shouldShowHdlCholesterol
        ? data?.hdlCholesterol || data?.tcHdlRatio || ""
        : "",
      nonHdlCholesterol: shouldShowNonHdlCholesterol
        ? data?.nonHdlCholesterol || ""
        : "",
      ldlCholesterol: shouldShowLdlCholesterol
        ? data?.ldlCholesterol || ""
        : "",
      triglycerides: shouldShowTriglycerides ? data?.triglycerides || "" : "",
      hba1c: shouldShowHba1c ? data?.hba1c || "" : "",
      serumCreatinine: shouldShowSerumCreatinine
        ? data?.serumCreatinine || ""
        : "",
      egfr: shouldShowEgfr ? data?.egfr || "" : "",
      tftConducted: shouldShowTftConducted ? data?.tftConducted || "" : "",
      lftConducted: shouldShowLftConducted ? data?.lftConducted || "" : "",
      fastingGlucose: shouldShowFastingGlucose
        ? data?.fastingGlucose || ""
        : "",
      creatinineClearance: shouldShowCreatinineClearance
        ? data?.creatinineClearance || ""
        : "",
      ntProBNP: shouldShowNtProBNP ? data?.ntProBNP || "" : "",
      acr: shouldShowAcr ? data?.acr || "" : "",
      // Table 3: ECG / ECHO / Spirometry default value is "No".
      ecgSinceLastReview: shouldShowEcgResult
        ? data?.ecgSinceLastReview || ""
        : "",
      ecgResult: shouldShowEcgResult ? data?.ecgResult || "" : "",
      echoSinceLastReview: shouldShowEchoSinceLastReview
        ? data?.echoSinceLastReview || ""
        : "",
      echoResult: shouldShowEchoResult ? data?.echoResult || "" : "",
      educationProvided: data?.educationProvided || "",
      referredToEducation: data?.referredToEducation || "",
      referredToDiabetesPrevention: data?.referredToDiabetesPrevention || "",
      referralDetails: data?.referralDetails || "",
      spirometrySinceLastReview: shouldShowSpirometrySinceLastReview
        ? data?.spirometrySinceLastReview || ""
        : "",
      spirometryResult: shouldShowSpirometryResult
        ? data?.spirometryResult || ""
        : "",
      diabetesRelatedAmputation: shouldShowDiabetesRelatedAmputation
        ? data?.diabetesRelatedAmputation || ""
        : "",
      amputationYear: shouldShowAmputationYear
        ? data?.amputationYear || ""
        : "",
      // Start unselected so no finding silently defaults to a pathological
      // value (KIN-049). The schema requires all six, so the clinician must
      // explicitly choose each before continuing.
      footAndLimbReviewFindings: shouldShowFootAndLimbReviewFindings
        ? data?.footAndLimbReviewFindings?.filter(Boolean) ?? []
        : [],
      footAndLimbReviewComment: shouldShowFootAndLimbReviewFindings
        ? data?.footAndLimbReviewComment || ""
        : "",
      retinaScreeningUpToDate: shouldShowRetinaScreeningUpToDate
        ? data?.retinaScreeningUpToDate || ""
        : "",
      referredForRetinaScreening: shouldShowReferredForRetinaScreening
        ? data?.referredForRetinaScreening || ""
        : "",
      exacerbationsLast6Months: hasAsthmaOrCopd
        ? data?.exacerbationsLast6Months || ""
        : "",
    },
  });

  const ecgPerformed = form.watch("ecgSinceLastReview");
  const echoPerformed = form.watch("echoSinceLastReview");
  const spirometryPerformed = form.watch("spirometrySinceLastReview");
  const referredToEducationValue = form.watch("referredToEducation");
  const referredToDiabetesValue = form.watch("referredToDiabetesPrevention");
  const isCdmReferralSelected =
    referredToEducationValue === "YES" ||
    (hasDiabetes && referredToDiabetesValue === "YES");

  const watchedTotalCholesterol = form.watch("totalCholesterol");
  const watchedHdlCholesterol = form.watch("hdlCholesterol");
  const isHba1cOutOfRange = isLabValueOutOfRange("hba1c", form.watch("hba1c"));

  useEffect(() => {
    if (!shouldShowNonHdlCholesterol) return;

    form.setValue(
      "nonHdlCholesterol",
      computeNonHdl(watchedTotalCholesterol, watchedHdlCholesterol),
      { shouldValidate: true }
    );
  }, [
    shouldShowNonHdlCholesterol,
    watchedTotalCholesterol,
    watchedHdlCholesterol,
    form,
  ]);

  const selectedFootAndLimbValues = form.watch("footAndLimbReviewFindings") || [];

  const handleFootAndLimbChange = (pairValues, selectedValue) => {
    const current = form.getValues("footAndLimbReviewFindings") || [];
    const withoutPair = current.filter((v) => !pairValues.includes(v));

    form.setValue("footAndLimbReviewFindings", [...withoutPair, selectedValue], {
      shouldValidate: true,
    });
  };

  const handleFormSubmit = (values) => {
    onContinue(values);
  };

  return (
    <ItemFormWrapper
      onBack={onBack}
      onContinue={form.handleSubmit(handleFormSubmit, onInvalidSubmit("TestAndMedicationForm"))}
      isReview={isReview}
    >
      <div
        className={style.itemWrapper}
        style={{
          marginBottom: "0px",
          paddingBottom: "0px",
          borderBottom: "none",
        }}
      >
        <FormProvider {...form}>
          <form
            className={style.addReferralForm}
            onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("TestAndMedicationForm"))}
          >
            {shouldShowInvestigation && (
              <div className={style.itemWrapper}>
                <h3>Investigations</h3>
                <div
                  className={style.inputGroupGrid3Columns}
                  style={{ marginTop: "16px" }}
                >
                  {shouldShowHemoglobin && (
                    <FormField name="haemoglobin">
                      <label className={style.addReferralFormLabel}>
                        Haemoglobin (FBC)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        inputMode="numeric"
                        onInput={(e) =>
                          (e.target.value = e.target.value.replace(
                            /[^0-9.]/g,
                            ""
                          ))
                        }
                        disabled={isReview}
                        {...form.register("haemoglobin")}
                      />
                    </FormField>
                  )}
                  {shouldShowTotalCholesterol && (
                    <FormField name="totalCholesterol">
                      <label className={style.addReferralFormLabel}>
                        Total Cholesterol (Lipids)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        inputMode="numeric"
                        disabled={isReview || (type === "interim" && form.watch("totalCholesterol") === "NOT_CLINICALLY_NECESSARY")}
                        value={
                          type === "interim" && form.watch("totalCholesterol") === "NOT_CLINICALLY_NECESSARY"
                            ? "Not clinically necessary"
                            : form.watch("totalCholesterol") ?? ""
                        }
                        onChange={(e) =>
                          form.setValue("totalCholesterol", e.target.value.replace(/[^0-9.]/g, ""), {
                            shouldValidate: true,
                          })
                        }
                      />
                      {type === "interim" && (
                        <label className={style.naCheckbox}>
                          <Checkbox
                            checked={form.watch("totalCholesterol") === "NOT_CLINICALLY_NECESSARY"}
                            disabled={isReview}
                            onChange={(e) =>
                              form.setValue(
                                "totalCholesterol",
                                e.target.checked ? "NOT_CLINICALLY_NECESSARY" : "",
                                { shouldValidate: true }
                              )
                            }
                          />
                          Not clinically necessary
                        </label>
                      )}
                    </FormField>
                  )}
                  {shouldShowHdlCholesterol && (
                    <FormField name="hdlCholesterol">
                      <label className={style.addReferralFormLabel}>
                        HDL Cholesterol (Lipids)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        inputMode="numeric"
                        disabled={isReview || (type === "interim" && form.watch("hdlCholesterol") === "NOT_CLINICALLY_NECESSARY")}
                        value={
                          type === "interim" && form.watch("hdlCholesterol") === "NOT_CLINICALLY_NECESSARY"
                            ? "Not clinically necessary"
                            : form.watch("hdlCholesterol") ?? ""
                        }
                        onChange={(e) =>
                          form.setValue("hdlCholesterol", e.target.value.replace(/[^0-9.]/g, ""), {
                            shouldValidate: true,
                          })
                        }
                      />
                      {type === "interim" && (
                        <label className={style.naCheckbox}>
                          <Checkbox
                            checked={form.watch("hdlCholesterol") === "NOT_CLINICALLY_NECESSARY"}
                            disabled={isReview}
                            onChange={(e) =>
                              form.setValue(
                                "hdlCholesterol",
                                e.target.checked ? "NOT_CLINICALLY_NECESSARY" : "",
                                { shouldValidate: true }
                              )
                            }
                          />
                          Not clinically necessary
                        </label>
                      )}
                    </FormField>
                  )}
                  {shouldShowNonHdlCholesterol && (
                    <FormField>
                      <label className={style.addReferralFormLabel}>
                        Non-HDL Cholesterol (Lipids)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        disabled
                        value={form.watch("nonHdlCholesterol") ?? ""}
                      />
                    </FormField>
                  )}
                  {shouldShowLdlCholesterol && (
                    <FormField name="ldlCholesterol">
                      <label className={style.addReferralFormLabel}>
                        LDL Cholesterol (Lipids)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        disabled={
                          isReview || form.watch("ldlCholesterol") === "Not Available"
                        }
                        value={
                          form.watch("ldlCholesterol") === "Not Available"
                            ? "Not available"
                            : form.watch("ldlCholesterol") ?? ""
                        }
                        onChange={(e) =>
                          form.setValue("ldlCholesterol", e.target.value, {
                            shouldValidate: true,
                          })
                        }
                      />
                      {!isLdlMandatory && (
                        <label className={style.naCheckbox}>
                          <Checkbox
                            checked={
                              form.watch("ldlCholesterol") === "Not Available"
                            }
                            disabled={isReview}
                            onChange={(e) =>
                              form.setValue(
                                "ldlCholesterol",
                                e.target.checked ? "Not Available" : "",
                                { shouldValidate: true }
                              )
                            }
                          />
                          Not available
                        </label>
                      )}
                    </FormField>
                  )}
                </div>
                <div
                  className={style.inputGroupGrid3Columns}
                  style={{ marginTop: "16px" }}
                >
                  {shouldShowTriglycerides && (
                    <FormField name="triglycerides">
                      <label className={style.addReferralFormLabel}>
                        Triglycerides (Lipids)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        inputMode="numeric"
                        disabled={isReview || (type === "interim" && form.watch("triglycerides") === "NOT_CLINICALLY_NECESSARY")}
                        value={
                          type === "interim" && form.watch("triglycerides") === "NOT_CLINICALLY_NECESSARY"
                            ? "Not clinically necessary"
                            : form.watch("triglycerides") ?? ""
                        }
                        onChange={(e) =>
                          form.setValue("triglycerides", e.target.value.replace(/[^0-9.]/g, ""), {
                            shouldValidate: true,
                          })
                        }
                      />
                      {type === "interim" && (
                        <label className={style.naCheckbox}>
                          <Checkbox
                            checked={form.watch("triglycerides") === "NOT_CLINICALLY_NECESSARY"}
                            disabled={isReview}
                            onChange={(e) =>
                              form.setValue(
                                "triglycerides",
                                e.target.checked ? "NOT_CLINICALLY_NECESSARY" : "",
                                { shouldValidate: true }
                              )
                            }
                          />
                          Not clinically necessary
                        </label>
                      )}
                    </FormField>
                  )}
                  {shouldShowHba1c && (
                    <FormField name="hba1c">
                      <label className={style.addReferralFormLabel}>
                        HbA1c
                      </label>
                      <div className={style.mmolInput}>
                        <input
                          type="text"
                          className="form-control"
                          inputMode="numeric"
                          onInput={(e) =>
                            (e.target.value = e.target.value.replace(
                              /[^0-9.]/g,
                              ""
                            ))
                          }
                          disabled={isReview}
                          {...form.register("hba1c")}
                        />
                      </div>
                      {isHba1cOutOfRange && (
                        <p className={style.hba1cWarning}>
                          {HBA1C_OUT_OF_RANGE_WARNING}
                        </p>
                      )}
                    </FormField>
                  )}
                </div>
                <div
                  className={style.inputGroupGrid3Columns}
                  style={{ marginTop: "16px" }}
                >
                  {shouldShowEgfr && (
                    <FormField name="egfr">
                      <label className={style.addReferralFormLabel}>
                        eGFR (Urea & Electrolytes)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        disabled={isReview || form.watch("egfr") === "Not Available"}
                        {...form.register("egfr")}
                        value={
                          form.watch("egfr") === "Not Available"
                            ? "Not available"
                            : form.watch("egfr") ?? ""
                        }
                        onChange={(e) =>
                          form.setValue("egfr", e.target.value, {
                            shouldValidate: true,
                          })
                        }
                      />
                      {!isEgfrMandatory && (
                        <label className={style.naCheckbox}>
                          <Checkbox
                            checked={form.watch("egfr") === "Not Available"}
                            disabled={isReview}
                            onChange={(e) =>
                              form.setValue(
                                "egfr",
                                e.target.checked ? "Not Available" : "",
                                { shouldValidate: true }
                              )
                            }
                          />
                          Not available
                        </label>
                      )}
                    </FormField>
                  )}
                  {shouldShowTftConducted && (
                    <FormField name="tftConducted">
                      <label className={style.addReferralFormLabel}>
                        Thyroid Function Test (TFT) Conducted?
                        <span className={style.requiredMark}>*</span>
                      </label>
                      <div className={style.radioGroup}>
                        {YES_NO_OPTIONS.map((option) => (
                          <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={
                              <Radio
                                disabled={isReview}
                                size="small"
                                checked={
                                  form.watch("tftConducted") === option.value
                                }
                                onChange={(e) =>
                                  form.setValue("tftConducted", e.target.value, {
                                    shouldValidate: true,
                                  })
                                }
                                sx={{
                                  "&.Mui-checked": { color: "var(--green-900)" },
                                }}
                              />
                            }
                            label={option.label}
                          />
                        ))}
                      </div>
                    </FormField>
                  )}
                  {shouldShowLftConducted && (
                    <FormField name="lftConducted">
                      <label className={style.addReferralFormLabel}>
                        Liver Function Test (LFT) Conducted?
                        <span className={style.requiredMark}>*</span>
                      </label>
                      <div className={style.radioGroup}>
                        {YES_NO_OPTIONS.map((option) => (
                          <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={
                              <Radio
                                disabled={isReview}
                                size="small"
                                checked={
                                  form.watch("lftConducted") === option.value
                                }
                                onChange={(e) =>
                                  form.setValue("lftConducted", e.target.value, {
                                    shouldValidate: true,
                                  })
                                }
                                sx={{
                                  "&.Mui-checked": { color: "var(--green-900)" },
                                }}
                              />
                            }
                            label={option.label}
                          />
                        ))}
                      </div>
                    </FormField>
                  )}
                  {shouldShowFastingGlucose && (
                    <FormField name="fastingGlucose">
                      <label className={style.addReferralFormLabel}>
                        Fasting Glucose
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        disabled={
                          isReview || form.watch("fastingGlucose") === "Not Available"
                        }
                        {...form.register("fastingGlucose")}
                        value={
                          form.watch("fastingGlucose") === "Not Available"
                            ? "Not available"
                            : form.watch("fastingGlucose") ?? ""
                        }
                        onChange={(e) =>
                          form.setValue("fastingGlucose", e.target.value, {
                            shouldValidate: true,
                          })
                        }
                      />
                      {!isFastingGlucoseMandatory && (
                        <label className={style.naCheckbox}>
                          <Checkbox
                            checked={
                              form.watch("fastingGlucose") === "Not Available"
                            }
                            disabled={isReview}
                            onChange={(e) =>
                              form.setValue(
                                "fastingGlucose",
                                e.target.checked ? "Not Available" : "",
                                { shouldValidate: true }
                              )
                            }
                          />
                          Not available
                        </label>
                      )}
                    </FormField>
                  )}
                  {shouldShowSerumCreatinine && (
                    <FormField name="serumCreatinine">
                      <label className={style.addReferralFormLabel}>
                        Serum Creatinine
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        inputMode="numeric"
                        onInput={(e) =>
                          (e.target.value = e.target.value.replace(
                            /[^0-9.]/g,
                            ""
                          ))
                        }
                        disabled={isReview}
                        {...form.register("serumCreatinine")}
                      />
                    </FormField>
                  )}
                  {shouldShowCreatinineClearance && (
                    <FormField name="creatinineClearance">
                      <label className={style.addReferralFormLabel}>
                        Creatinine Clearance
                      </label>
                      <input
                        type="hidden"
                        {...form.register("creatinineClearance")}
                      />
                      <CalculateInputTrigger
                        value={form.watch("creatinineClearance")}
                        onClick={() => setIsCrclModalOpen(true)}
                        disabled={isReview}
                      />
                      <p className={style.referralPrompt}>
                        {CREATININE_CLEARANCE_HINT}
                      </p>
                    </FormField>
                  )}
                  {shouldShowNtProBNP && (
                    <FormField name="ntProBNP">
                      <label className={style.addReferralFormLabel}>
                        B-type Natriuretic Test (NT Pro BNP)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        disabled={isReview || form.watch("ntProBNP") === "Not Available"}
                        {...form.register("ntProBNP")}
                        value={
                          form.watch("ntProBNP") === "Not Available"
                            ? "Not available"
                            : form.watch("ntProBNP") ?? ""
                        }
                        onChange={(e) =>
                          form.setValue("ntProBNP", e.target.value, {
                            shouldValidate: true,
                          })
                        }
                      />
                      {!isNtProBNPMandatory && (
                        <label className={style.naCheckbox}>
                          <Checkbox
                            checked={form.watch("ntProBNP") === "Not Available"}
                            disabled={isReview}
                            onChange={(e) =>
                              form.setValue(
                                "ntProBNP",
                                e.target.checked ? "Not Available" : "",
                                { shouldValidate: true }
                              )
                            }
                          />
                          Not available
                        </label>
                      )}
                    </FormField>
                  )}
                  {shouldShowAcr && (
                    <FormField name="acr">
                      <label className={style.addReferralFormLabel}>
                        Albumin/Creatinine Ratio (ACR)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        inputMode="numeric"
                        disabled={isReview || form.watch("acr") === "Not Available"}
                        value={
                          form.watch("acr") === "Not Available"
                            ? "Not available"
                            : form.watch("acr") ?? ""
                        }
                        onChange={(e) =>
                          form.setValue("acr", e.target.value.replace(/[^0-9.]/g, ""), {
                            shouldValidate: true,
                          })
                        }
                      />
                      {!isAcrMandatory && (
                        <label className={style.naCheckbox}>
                          <Checkbox
                            checked={form.watch("acr") === "Not Available"}
                            disabled={isReview}
                            onChange={(e) =>
                              form.setValue(
                                "acr",
                                e.target.checked ? "Not Available" : "",
                                { shouldValidate: true }
                              )
                            }
                          />
                          Not available
                        </label>
                      )}
                    </FormField>
                  )}
                </div>
                {/* NEW 19-03-2024 (CDM Treatment V3.1 dataset, row 63b): the
                    NTproBNP indications alert must show on EVERY review, so it
                    is rendered independently of the NT-proBNP field's own
                    visibility (matrix-gated). Placed under the test-results grid
                    as a full-width note, not tied to a single column. */}
                <p className={style.ntProBnpAlert}>{NT_PROBNP_ALERT}</p>
              </div>
            )}
            {shouldShowDiagnosticinvestigation && (
              <div className={style.itemWrapper}>
                <h3>Diagnostic Investigations</h3>
                <div
                  className={style.diagnosticColumns}
                  style={{ marginTop: "16px" }}
                >
                  {shouldShowEcgSinceLastReview && (
                    <div className={style.diagnosticGroup}>
                      <FormField name="ecgSinceLastReview">
                        <label className={style.addReferralFormLabel}>
                          {type === "reg"
                            ? "Has the patient had an ECG?"
                            : "Has the patient had an ECG since the last review?"}
                        </label>
                        <Select
                          options={YES_NO_OPTIONS}
                          placeholder="Select"
                          value={form.watch("ecgSinceLastReview")}
                          onChange={(value) => {
                            form.setValue("ecgSinceLastReview", value, {
                              shouldValidate: true,
                            });
                            if (value !== "YES") {
                              form.setValue("ecgResult", "", {
                                shouldValidate: true,
                              });
                            }
                          }}
                          disabled={isReview}
                          required
                        />
                      </FormField>
                      {ecgPerformed === "YES" && (
                        <FormField name="ecgResult">
                          <label className={style.addReferralFormLabel}>
                            ECG Result
                          </label>
                          <Select
                            options={ECG_RESULT_OPTIONS}
                            placeholder="Select"
                            value={form.watch("ecgResult")}
                            onChange={(value) =>
                              form.setValue("ecgResult", value, {
                                shouldValidate: true,
                              })
                            }
                            disabled={isReview}
                            required
                          />
                        </FormField>
                      )}
                    </div>
                  )}
                  {shouldShowEchoSinceLastReview && (
                    <div className={style.diagnosticGroup}>
                      <FormField name="echoSinceLastReview">
                        <label className={style.addReferralFormLabel}>
                          {type === "reg"
                            ? "Has the patient had an Echocardiography?"
                            : "Has the patient had an Echocardiography since the last review?"}
                        </label>
                        <Select
                          options={YES_NO_OPTIONS}
                          placeholder="Select"
                          value={form.watch("echoSinceLastReview")}
                          onChange={(value) => {
                            form.setValue("echoSinceLastReview", value, {
                              shouldValidate: true,
                            });
                            if (value !== "YES") {
                              form.setValue("echoResult", "", {
                                shouldValidate: true,
                              });
                            }
                          }}
                          disabled={isReview}
                          required
                        />
                      </FormField>
                      {echoPerformed === "YES" && (
                        <FormField name="echoResult">
                          <label className={style.addReferralFormLabel}>
                            Echocardiography Result
                          </label>
                          <Select
                            options={ECHO_RESULT_OPTIONS}
                            placeholder="Select"
                            value={form.watch("echoResult")}
                            onChange={(value) =>
                              form.setValue("echoResult", value, {
                                shouldValidate: true,
                              })
                            }
                            disabled={isReview}
                            required
                          />
                        </FormField>
                      )}
                    </div>
                  )}
                  {shouldShowSpirometrySinceLastReview && (
                    <div className={style.diagnosticGroup}>
                      <FormField name="spirometrySinceLastReview">
                        <label className={style.addReferralFormLabel}>
                          {type === "reg"
                            ? "Has the patient had Spirometry?"
                            : "Has the patient had Spirometry since the last review?"}
                        </label>
                        <Select
                          options={YES_NO_OPTIONS}
                          placeholder="Select"
                          value={form.watch("spirometrySinceLastReview")}
                          onChange={(value) => {
                            form.setValue("spirometrySinceLastReview", value, {
                              shouldValidate: true,
                            });
                            if (value !== "YES") {
                              form.setValue("spirometryResult", "", {
                                shouldValidate: true,
                              });
                            }
                          }}
                          disabled={isReview}
                          required
                        />
                      </FormField>
                      {spirometryPerformed === "YES" && (
                        <FormField name="spirometryResult">
                          <label className={style.addReferralFormLabel}>
                            Spirometry Result
                          </label>
                          <Select
                            options={SPIROMETRY_RESULT_OPTIONS}
                            placeholder="Select"
                            value={form.watch("spirometryResult")}
                            onChange={(value) =>
                              form.setValue("spirometryResult", value, {
                                shouldValidate: true,
                              })
                            }
                            disabled={isReview}
                            required
                          />
                        </FormField>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className={style.itemWrapper}>
              <h3>Patient Education</h3>
              <div
                className={style.inputGroupGrid3Columns}
                style={{ marginTop: "16px" }}
              >
                <FormField name="educationProvided">
                  <label className={style.addReferralFormLabel}>
                    Education Provided
                  </label>
                  <Select
                    options={EDUCATION_PROVIDED_OPTIONS}
                    placeholder="Select"
                    value={form.watch("educationProvided")}
                    onChange={(value) =>
                      form.setValue("educationProvided", value, {
                        shouldValidate: true,
                      })
                    }
                    disabled={isReview}
                    required
                  />
                </FormField>
                <FormField name="referredToEducation">
                  <label className={style.addReferralFormLabel}>
                    Referred to Education
                  </label>
                  <Select
                    options={REFERRED_TO_EDUCATION_OPTIONS}
                    placeholder="Select"
                    value={form.watch("referredToEducation")}
                    onChange={(value) =>
                      form.setValue("referredToEducation", value, {
                        shouldValidate: true,
                      })
                    }
                    disabled={isReview}
                    required
                  />
                  {(hasDiabetes ||
                    registrationReasons.includes("COPD") ||
                    registrationReasons.includes("HEART_FAILURE")) && (
                    <p className={style.referralPrompt}>
                      Consider referral to a structured education programme:
                      {registrationReasons.includes("COPD") &&
                        " COPD → pulmonary rehabilitation;"}
                      {registrationReasons.includes("HEART_FAILURE") &&
                        " Heart failure → cardiac rehabilitation;"}
                      {hasDiabetes && (
                        <>
                          {" "}
                          Diabetes →{" "}
                          <a
                            href="https://register.enthuse.com/ps/event/DiabetesSupport%20course"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Diabetes Structured Patient Education (SPE)
                          </a>
                          .
                        </>
                      )}
                    </p>
                  )}
                </FormField>
                {!!hasDiabetes && (
                  <FormField name="referredToDiabetesPrevention">
                    <label className={style.addReferralFormLabel}>
                      Referred to Diabetes Prevention
                    </label>
                    <Select
                      options={REFERRED_TO_DIABETES_PREVENTION_OPTIONS}
                      placeholder="Select"
                      value={form.watch("referredToDiabetesPrevention")}
                      onChange={(value) =>
                        form.setValue("referredToDiabetesPrevention", value, {
                          shouldValidate: true,
                        })
                      }
                      disabled={isReview}
                      required
                    />
                  </FormField>
                )}
                {!!isCdmReferralSelected && (
                  <FormField name="referralDetails">
                    <label className={style.addReferralFormLabel}>
                      Referral Details
                    </label>
                    <Select
                      options={REFERRAL_DETAILS_OPTIONS}
                      placeholder="Select"
                      value={form.watch("referralDetails")}
                      onChange={(value) =>
                        form.setValue("referralDetails", value, {
                          shouldValidate: true,
                        })
                      }
                      disabled={isReview}
                      required
                    />
                  </FormField>
                )}
              </div>
            </div>
            {data.diabetesType2 && (
              <div className={style.itemWrapper}>
                <h3>Diabetes Specifics</h3>
                <div
                  className={style.inputGroupGrid3Columns}
                  style={{ marginTop: "16px" }}
                >
                  {shouldShowDiabetesRelatedAmputation && (
                    <FormField name="diabetesRelatedAmputation">
                      <label className={style.addReferralFormLabel}>
                        Diabetes Related Amputation
                        <span className={style.requiredMark}>*</span>
                      </label>
                      <div className={style.radioGroup}>
                        {YES_NO_OPTIONS.map((option) => (
                          <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={
                              <Radio
                                disabled={isReview}
                                size="small"
                                checked={
                                  form.watch("diabetesRelatedAmputation") ===
                                  option.value
                                }
                                onChange={(e) =>
                                  form.setValue(
                                    "diabetesRelatedAmputation",
                                    e.target.value,
                                    { shouldValidate: true }
                                  )
                                }
                                sx={{
                                  "&.Mui-checked": { color: "var(--green-900)" },
                                }}
                              />
                            }
                            label={option.label}
                          />
                        ))}
                      </div>
                    </FormField>
                  )}
                  {shouldShowAmputationYear && (
                    <FormField name="amputationYear">
                      <label className={style.addReferralFormLabel}>
                        Amputation Year
                      </label>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={1900}
                        max={new Date().getFullYear()}
                        step={1}
                        placeholder="e.g. 2021"
                        className="form-control"
                        disabled={
                          isReview ||
                          form.watch("diabetesRelatedAmputation") === "NO"
                        }
                        {...form.register("amputationYear")}
                      />
                    </FormField>
                  )}
                  {shouldShowFootAndLimbReviewFindings && (
                    <FormField name="footAndLimbReviewFindings">
                      <label className={style.addReferralFormLabel}>
                        Foot and Limb Review Findings
                      </label>
                      <div className={style.footAndLimbGrid}>
                        {FOOT_AND_LIMB_REVIEW_PAIRS.map((pair) => (
                          <div key={pair.label} className={style.footAndLimbRow}>
                            <span className={style.footAndLimbRowLabel}>{pair.label}</span>
                            <div className={style.footAndLimbRadioGroup}>
                              {pair.values.map((value, index) => (
                                <FormControlLabel
                                  key={value}
                                  value={value}
                                  label={pair.optionLabels[index]}
                                  control={
                                    <Radio
                                      size="small"
                                      disabled={isReview}
                                      checked={selectedFootAndLimbValues.includes(value)}
                                      onChange={() => handleFootAndLimbChange(pair.values, value)}
                                      sx={{ "&.Mui-checked": { color: "var(--green-900)" } }}
                                      inputProps={{ "data-testid": `foot-limb-radio-${value}` }}
                                    />
                                  }
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </FormField>
                  )}
                  {shouldShowFootAndLimbReviewFindings && (
                    <FormField name="footAndLimbReviewComment">
                      <label className={style.addReferralFormLabel}>
                        Foot and Limb Review Comment
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        disabled={isReview}
                        {...form.register("footAndLimbReviewComment")}
                      />
                    </FormField>
                  )}
                  {shouldShowRetinaScreeningUpToDate && (
                    <FormField name="retinaScreeningUpToDate">
                      <label className={style.addReferralFormLabel}>
                        Is the Patient up to date with Retinal Screening?
                      </label>
                      <Select
                        options={YES_NO_OPTIONS}
                        placeholder="Select"
                        value={form.watch("retinaScreeningUpToDate")}
                        onChange={(value) => {
                          form.setValue("retinaScreeningUpToDate", value, {
                            shouldValidate: true,
                          });
                          // Ref 46 only applies when not up to date — clear it otherwise.
                          if (value !== "NO") {
                            form.setValue("referredForRetinaScreening", "", {
                              shouldValidate: true,
                            });
                          }
                        }}
                        disabled={isReview}
                        required
                      />
                    </FormField>
                  )}
                  {shouldShowReferredForRetinaScreening &&
                    form.watch("retinaScreeningUpToDate") === "NO" && (
                    <FormField name="referredForRetinaScreening">
                      <label className={style.addReferralFormLabel}>
                        Was the Patient Referred for Retinal Screening?
                      </label>
                      <Select
                        options={YES_NO_PATIENT_DECLINED_OPTIONS}
                        placeholder="Select"
                        value={form.watch("referredForRetinaScreening")}
                        onChange={(value) =>
                          form.setValue("referredForRetinaScreening", value, {
                            shouldValidate: true,
                          })
                        }
                        disabled={isReview}
                        required
                      />
                    </FormField>
                  )}
                </div>
              </div>
            )}
            {hasAsthmaOrCopd && (
              <div
                className={style.itemWrapper}
                style={{
                  marginBottom: "0px",
                  paddingBottom: "0px",
                  borderBottom: "none",
                }}
              >
                <h3>Asthma/COPD Specifics</h3>
                <div
                  className={style.inputGroupGrid2Columns}
                  style={{ marginTop: "16px" }}
                >
                  <FormField name="exacerbationsLast6Months">
                    <label className={style.addReferralFormLabel}>
                      How many exacerbations has the patient had in the last 6
                      months?
                    </label>
                    <Select
                      options={COUNT_OPTIONS}
                      placeholder="Select"
                      value={form.watch("exacerbationsLast6Months")}
                      onChange={(value) =>
                        form.setValue("exacerbationsLast6Months", value, {
                          shouldValidate: true,
                        })
                      }
                      disabled={isReview}
                      required
                    />
                    <p className={style.referralPrompt}>
                      (i.e. requiring treatment with antibiotics and/or Steroids)
                    </p>
                  </FormField>
                </div>
              </div>
            )}
          </form>
        </FormProvider>
      </div>
      {shouldShowCreatinineClearance && (
        <CreatinineClearanceCalculatorModal
          isOpen={isCrclModalOpen}
          onClose={() => setIsCrclModalOpen(false)}
          onConfirm={({ creatinineClearance, serumCreatinine }) => {
            form.setValue("creatinineClearance", creatinineClearance, {
              shouldValidate: true,
            });
            if (serumCreatinine) {
              form.setValue("serumCreatinine", serumCreatinine, {
                shouldValidate: true,
              });
            }
          }}
          prefillWeight={data?.weight}
          prefillSerumCreatinine={form.watch("serumCreatinine")}
          prefillHeight={data?.height}
        />
      )}
    </ItemFormWrapper>
  );
};
